import { AlertCircle, Expand, RefreshCw } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'

import type { ResourceType } from '@/types/resource.type'

interface DocxContentViewerProps {
  resource: ResourceType
  className?: string
  isDownloadable?: boolean
  onLoadingComplete?: () => Promise<void>
}

/**
 * Renders DOC/DOCX files via the Microsoft Office Online viewer (same embed
 * service used by the PPTX viewer). No additional client-side dependencies
 * are required — the file URL is passed to the Office Online iframe.
 */
export function DocxContentViewer({
  resource,
  className,
  isDownloadable = false,
  onLoadingComplete,
}: DocxContentViewerProps) {
  const iframeContainerRef = React.useRef<HTMLDivElement>(null)
  const iframeRef = React.useRef<HTMLIFrameElement>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const loadTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const completionCallbackRef = React.useRef(false)

  const viewerUrl = React.useMemo(
    () => `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(resource.url)}`,
    [resource.url]
  )

  const handleIframeLoad = React.useCallback(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current)
    }

    loadTimeoutRef.current = setTimeout(() => {
      setLoading(false)
      setError(null)
      if (!completionCallbackRef.current && onLoadingComplete) {
        setTimeout(() => {
          completionCallbackRef.current = true
          onLoadingComplete()
        }, 2000)
      }
    }, 2000)
  }, [onLoadingComplete])

  // Maximum loading timeout — stop spinner after 30s even if iframe hasn't
  // signalled load (e.g. very large documents).
  React.useEffect(() => {
    const maxTimeout = setTimeout(() => {
      if (loading) setLoading(false)
    }, 30000)

    return () => {
      clearTimeout(maxTimeout)
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)
    }
  }, [loading])

  const handleFullscreen = () => {
    if (iframeContainerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        iframeContainerRef.current.requestFullscreen()
      }
    }
  }

  const handleRetry = React.useCallback(() => {
    setError(null)
    setLoading(true)
    completionCallbackRef.current = false
    if (iframeRef.current) {
      iframeRef.current.src = viewerUrl
    }
  }, [viewerUrl])

  if (error) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-600" />
          <p className="mb-2 font-semibold text-red-600">Error loading document</p>
          <p className="mb-4 text-sm text-gray-600">{error}</p>
          <div className="flex gap-2">
            <Button onClick={handleRetry} variant="outline">
              <RefreshCw className="mr-2 size-4" />
              Retry
            </Button>
            {isDownloadable && (
              <a
                href={resource.url}
                download
                className="flex items-center gap-2 rounded border border-black bg-white px-4 py-2 text-black transition hover:bg-gray-200"
              >
                Download File
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('relative h-screen w-full', className)} ref={iframeContainerRef}>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-90">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-gray-900"></div>
            <p className="text-gray-600">Loading document...</p>
            <p className="mt-2 text-sm text-gray-500">Large files may take longer to load</p>
          </div>
        </div>
      )}
      <div className="h-full w-full overflow-hidden">
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          className="w-full"
          style={{
            height: '100%',
            outline: 'none',
            border: 'none',
          }}
          frameBorder="0"
          onLoad={handleIframeLoad}
          title="Document Viewer"
          allow="fullscreen"
        >
          This browser does not support document viewing.
        </iframe>
      </div>
      <Button
        onClick={handleFullscreen}
        className="absolute right-2 top-2 z-20 rounded bg-gray-600 p-2 text-white transition hover:bg-gray-700"
        size="icon"
        variant="ghost"
        aria-label="Toggle fullscreen"
      >
        <Expand className="size-6" />
      </Button>
    </div>
  )
}
